import { Link, usePage } from "@inertiajs/react";
import {
  AlertCircle,
  Brain,
  Crown,
  Loader2,
  Lock,
  Monitor,
  Pencil,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { RequestForm } from "@/bizminer/App/InsightRequests/_components/RequestForm";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import {
  AnalysisProgressBar,
  type AnalysisStatus,
} from "@/components/analysis/AnalysisProgressBar";
import { CompanyLogo } from "@/components/CompanyLogo.tsx";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDashboardChannels } from "@/hooks/useDashboardChannels";
import {
  bizminer_app_pillar_insight_insight_request_path,
  bizminer_app_pillar_insight_path,
  edit_bizminer_app_account_path,
} from "@/routes";
import type { BizminerInertiaSharedProps } from "@/types/inertia";
import type {
  BizminerAccount,
  BizminerPillarInsight,
} from "@/types/serializers";

interface Props {
  account: BizminerAccount;
  pillar_insights: BizminerPillarInsight[];
}

const iconMap = {
  users: Users,
  "user-check": UserCheck,
  monitor: Monitor,
  crown: Crown,
  brain: Brain,
  zap: Zap,
};

type PillarStatus = "unlocked" | "failed" | "in_progress" | "locked";

function getPillarStatus(pillar: BizminerPillarInsight): PillarStatus {
  if (pillar.unlocked) return "unlocked";
  if (pillar.insight_request?.status === "failed") return "failed";
  if (pillar.insight_request?.in_progress) return "in_progress";
  return "locked";
}

function UnlockedContent({ pillar }: { pillar: BizminerPillarInsight }) {
  return (
    <CardContent>
      <div className="flex items-center justify-between text-sm">
        <span>
          <span className="text-muted-foreground">Improvement Levers: </span>
          <span className="font-semibold">{pillar.improvement_lever_count}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Score: </span>
          <span className="font-semibold">{pillar.analysis.account_score}</span>
        </span>
      </div>
    </CardContent>
  );
}

function FailedContent({ pillar }: { pillar: BizminerPillarInsight }) {
  const failedAtStage = pillar.insight_request?.failed_at_engine_status;

  return (
    <CardContent className="mt-auto space-y-4">
      {/* Progress Bar showing where it failed */}
      <AnalysisProgressBar status="failed" failedAtStage={failedAtStage} />

      {/* View Error Button */}
      <Link
        href={bizminer_app_pillar_insight_insight_request_path(
          pillar.id,
          pillar.insight_request?.id,
        )}
      >
        <Button className="w-full" size="sm" variant="destructive">
          <AlertCircle className="h-3 w-3 mr-2" />
          View Error
        </Button>
      </Link>
    </CardContent>
  );
}

function InProgressContent({ pillar }: { pillar: BizminerPillarInsight }) {
  const status = pillar.insight_request?.status;
  const engineStatus = pillar.insight_request?.engine_status;
  const failedAtStage = pillar.insight_request?.failed_at_engine_status;

  const progressStatus: AnalysisStatus =
    status === "analyzed"
      ? "analyzed"
      : status === "completed"
        ? "completed"
        : (engineStatus as AnalysisStatus) || "collecting_data";

  return (
    <CardContent className="mt-auto space-y-4">
      {/* Progress Bar */}
      <AnalysisProgressBar
        status={progressStatus}
        failedAtStage={failedAtStage}
      />

      {/* View Progress Button */}
      <Link
        href={bizminer_app_pillar_insight_insight_request_path(
          pillar.id,
          pillar.insight_request?.id,
        )}
      >
        <Button className="w-full" size="sm" variant="outline">
          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
          View Progress
        </Button>
      </Link>
    </CardContent>
  );
}

function LockedContent({
  onRequestAnalysis,
  canRequestAnalysis,
}: {
  onRequestAnalysis: () => void;
  canRequestAnalysis: boolean;
}) {
  return (
    <CardContent className="mt-auto space-y-4">
      {canRequestAnalysis ? (
        <Button className="w-full" size="sm" onClick={onRequestAnalysis}>
          Request Analysis
        </Button>
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="w-full">
                <Button className="w-full" size="sm" disabled>
                  <Lock className="h-3 w-3 mr-2" />
                  Request Analysis
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Contact your admin to request analysis access</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </CardContent>
  );
}

function StatusIcon({ status }: { status: PillarStatus }) {
  return (
    <div className="flex items-center gap-1">
      {status === "in_progress" && (
        <Loader2 className="h-4 w-4 text-primary animate-spin" />
      )}
      {status === "failed" && (
        <AlertCircle className="h-4 w-4 text-destructive" />
      )}
      {status === "locked" && (
        <Lock className="h-4 w-4 text-muted-foreground" />
      )}
    </div>
  );
}

function PillarCardContent({
  status,
  pillar,
  onRequestAnalysis,
  canRequestAnalysis,
}: {
  status: PillarStatus;
  pillar: BizminerPillarInsight;
  onRequestAnalysis?: () => void;
  canRequestAnalysis: boolean;
}) {
  switch (status) {
    case "unlocked":
      return <UnlockedContent pillar={pillar} />;
    case "failed":
      return <FailedContent pillar={pillar} />;
    case "in_progress":
      return <InProgressContent pillar={pillar} />;
    case "locked":
      return (
        <LockedContent
          onRequestAnalysis={onRequestAnalysis!}
          canRequestAnalysis={canRequestAnalysis}
        />
      );
  }
}

function PillarCard({
  pillar,
  onRequestAnalysis,
  canRequestAnalysis,
}: {
  pillar: BizminerPillarInsight;
  onRequestAnalysis?: () => void;
  canRequestAnalysis: boolean;
}) {
  const IconComponent = iconMap[pillar.icon as keyof typeof iconMap] || Users;
  const status = getPillarStatus(pillar);

  return (
    <Card
      className={`relative h-full flex flex-col ${pillar.unlocked ? "cursor-pointer transition-[box-shadow,background-color,border-color] hover:shadow-md hover:bg-muted/20 hover:border-foreground/20" : ""}`}
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex size-10 items-center justify-center rounded-lg border border-border/50 bg-muted/30">
              <IconComponent className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-base">{pillar.title}</CardTitle>
          </div>
          {status === "unlocked" ? (
            pillar.report_date && (
              <span className="text-xs text-muted-foreground">
                {pillar.report_date}
              </span>
            )
          ) : (
            <StatusIcon status={status} />
          )}
        </div>
        <CardDescription>{pillar.description}</CardDescription>
      </CardHeader>
      <PillarCardContent
        status={status}
        pillar={pillar}
        onRequestAnalysis={onRequestAnalysis}
        canRequestAnalysis={canRequestAnalysis}
      />
    </Card>
  );
}

function StrategicPillarsGrid({
  pillars,
  onRequestAnalysis,
  canRequestAnalysis,
}: {
  pillars: BizminerPillarInsight[];
  onRequestAnalysis: (pillar: BizminerPillarInsight) => void;
  canRequestAnalysis: boolean;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {pillars.map((pillar) => {
        const status = getPillarStatus(pillar);
        const cardKey = `${pillar.id}-${status}-${pillar.insight_request?.status || "none"}`;

        return pillar.unlocked ? (
          <Link
            key={cardKey}
            href={bizminer_app_pillar_insight_path(pillar.id)}
          >
            <PillarCard
              pillar={pillar}
              canRequestAnalysis={canRequestAnalysis}
            />
          </Link>
        ) : (
          <div key={cardKey}>
            <PillarCard
              pillar={pillar}
              onRequestAnalysis={() => onRequestAnalysis(pillar)}
              canRequestAnalysis={canRequestAnalysis}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function Index({
  account,
  pillar_insights: initialPillarInsights,
}: Props) {
  const {
    props: { permissions },
  } = usePage<BizminerInertiaSharedProps>();

  const [pillarInsights, setPillarInsights] = useState(initialPillarInsights);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPillar, setSelectedPillar] =
    useState<BizminerPillarInsight | null>(null);

  // Subscribe to all active insight requests
  useDashboardChannels(
    pillarInsights,
    (updatedInsightRequest, updatedPillarInsight) => {
      // Update the pillar insight that matches this insight request
      setPillarInsights((prevPillars) =>
        prevPillars.map((pillar) => {
          if (pillar.insight_request?.id === updatedInsightRequest.id) {
            // If we received the full pillar_insight, use it
            if (updatedPillarInsight) {
              return updatedPillarInsight;
            }
            // Otherwise, just update the insight_request
            return {
              ...pillar,
              insight_request: updatedInsightRequest,
            };
          }
          return pillar;
        }),
      );
    },
  );

  const handleRequestAnalysis = (pillar: BizminerPillarInsight) => {
    setSelectedPillar(pillar);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPillar(null);
  };

  const handleSuccess = () => {
    handleCloseModal();
  };

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CompanyLogo
                logoUrl={account.logo_url}
                name={account.name}
                className="size-12 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {account.name}
                </h1>
                <p className="text-muted-foreground">
                  Manage your strategic pillars and track improvements
                </p>
              </div>
            </div>
            {permissions.can_manage_accounts && (
              <Link href={edit_bizminer_app_account_path()}>
                <Button variant="outline" size="sm">
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </Link>
            )}
          </div>
        </div>

        <StrategicPillarsGrid
          pillars={pillarInsights}
          onRequestAnalysis={handleRequestAnalysis}
          canRequestAnalysis={permissions.can_request_analysis}
        />

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 overflow-y-auto">
            {selectedPillar && (
              <RequestForm
                pillar_insight={selectedPillar}
                account={account}
                onSuccess={handleSuccess}
                onCancel={handleCloseModal}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </BizminerLayout>
  );
}
